import React from "react";
import { Button } from "@excalidraw/excalidraw/components/Button";
import { ExportImageIcon } from "@excalidraw/excalidraw/components/icons";
import { useI18n } from "@excalidraw/excalidraw/i18n";
import { useExcalidrawSetAppState } from "@excalidraw/excalidraw/components/App";

import "./NavButton.scss";

export type NavButtonVariant = "icon-text" | "text-only" | "icon-only";

export interface NavButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: NavButtonVariant;
  text?: string;
  icon?: React.ReactNode;
  action?: () => void;
}

const NavButton = ({
  variant = "icon-text",
  text,
  icon = ExportImageIcon,
  className = "",
  action,
  ...rest
}: NavButtonProps) => {
  const { t } = useI18n();
  const setAppState = useExcalidrawSetAppState();

  const handleClick = () => {
    if (action) {
      action();
    } else {
      // Default export action
      setAppState({ openDialog: { name: "imageExport" } });
    }
  };

  const displayText = text || t("buttons.exportImage");
  const buttonClass = `nav-button nav-button--${variant} ${className}`.trim();

  const renderContent = () => {
    switch (variant) {
      case "icon-only":
        return icon;
      case "text-only":
        return <span className="nav-button-text">{displayText}</span>;
      case "icon-text":
      default:
        return (
          <>
            {icon}
            <span className="nav-button-text">{displayText}</span>
          </>
        );
    }
  };

  return (
    <Button
      {...rest}
      className={buttonClass}
      type="button"
      onSelect={handleClick}
      title={displayText}
      aria-label={displayText}
    >
      {renderContent()}
    </Button>
  );
};

export default NavButton;

// Example usage:
// <NavButton /> // Default: icon + "Export" text (export action)
// <NavButton variant="icon-only" /> // Just the export icon
// <NavButton variant="text-only" text="Save Image" action={customAction} /> // Just custom text with custom action
// <NavButton variant="icon-text" text="Download" icon={CustomIcon} action={downloadAction} /> // Custom icon + text + action

// For backward compatibility, create an ExportButton wrapper
export const ExportButton = (props: Omit<NavButtonProps, "action">) => (
  <NavButton {...props} />
);
